import React, { useState, useEffect } from 'react';
import { getCurrentUser, updateMyProfile, uploadAvatar, deleteAvatar, getAvatarUrl } from '../services/api';
import { ProfileSkeleton } from '../components/SkeletonLoader';

/* ─── Eye icon helpers ───────────────────────────────────────── */
const EyeOff = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-5 0-9-4-9-7s4-7 9-7a9.96 9.96 0 015.46 1.625M15 12a3 3 0 11-6 0 3 3 0 016 0zm6 0c0 3-4 7-9 7" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 3l18 18" />
  </svg>
);
const EyeOn = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);
const CameraIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

// PasswordField is defined outside AdminProfile so React sees it as a
// stable component reference and doesn't unmount it on every render
const PasswordField = ({ label, name, value, onChange, showPw, onTogglePw, error }) => (
  <div>
    <label className="block text-[13px] font-bold text-gray-800 mb-2">{label}</label>
    <div className="relative">
      <input
        type={showPw ? 'text' : 'password'}
        name={name}
        value={value}
        onChange={onChange}
        className={`w-full bg-[#f8fafc] border ${error ? 'border-red-400' : 'border-gray-100'} rounded-xl px-5 py-3.5 pr-12 text-[14px] text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#086C53]/30 transition`}
      />
      <button
        type="button"
        onClick={onTogglePw}
        className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition"
      >
        {showPw ? <EyeOff /> : <EyeOn />}
      </button>
    </div>
    {error && <p className="text-red-500 text-[11px] mt-1">{error}</p>}
  </div>
);

/* ─── Main profile page ──────────────────────────────────────── */
const AdminProfile = () => {
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [profile, setProfile] = useState({
    userId: null,
    fullName: '',
    email: '',
    position: 'Coordinator',
    hasAvatar: false,
  });
  const [form, setForm] = useState({
    fullName: '',
    password: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPw, setShowPw] = useState({
    password: false,
    newPassword: false,
    confirmPassword: false,
  });
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Avatar upload state
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [avatarError, setAvatarError] = useState('');
  const [deletingAvatar, setDeletingAvatar] = useState(false);
  const [avatarVersion, setAvatarVersion] = useState(Date.now());

  // Fetches the real logged-in admin user on load
  const fetchProfile = async () => {
    try {
      setLoading(true);
      const res = await getCurrentUser();
      const user = res.data;
      const newProfile = {
        userId: user.user_id,
        fullName: user.full_name,
        email: user.slsu_email,
        position: 'Coordinator',
        hasAvatar: !!user.profile_picture_path,
      };
      setProfile(newProfile);
      setForm((prev) => ({ ...prev, fullName: newProfile.fullName }));
    } catch (error) {
      console.error('Failed to fetch admin profile:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const togglePw = (field) =>
    setShowPw((prev) => ({ ...prev, [field]: !prev[field] }));

  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const validate = () => {
    const errs = {};
    if (!form.fullName.trim()) errs.fullName = 'Full name is required.';
    if (form.newPassword && form.newPassword !== form.confirmPassword)
      errs.confirmPassword = 'Passwords do not match.';
    if (form.newPassword && !form.password)
      errs.password = 'Enter your current password to set a new one.';
    return errs;
  };

  // Saves the real changes to the backend — name and/or password
  const handleSave = async () => {
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setErrors({});
    setSubmitError('');
    setSaving(true);

    try {
      const payload = { full_name: form.fullName };
      if (form.newPassword) {
        payload.current_password = form.password;
        payload.new_password = form.newPassword;
      }

      const res = await updateMyProfile(payload);

      // Keeps localStorage in sync so the topbar reflects the new name immediately
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      localStorage.setItem('user', JSON.stringify({ ...storedUser, full_name: res.data.full_name }));

      setProfile((prev) => ({ ...prev, fullName: res.data.full_name }));
      setSaved(true);
      setIsEditing(false);
      setTimeout(() => setSaved(false), 3000);
      setForm((prev) => ({ ...prev, password: '', newPassword: '', confirmPassword: '' }));
    } catch (error) {
      if (error.response && error.response.data && error.response.data.detail) {
        setSubmitError(error.response.data.detail);
      } else {
        setSubmitError('Failed to save changes. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setErrors({});
    setSubmitError('');
    setForm({ fullName: profile.fullName, password: '', newPassword: '', confirmPassword: '' });
  };

  // Validates and uploads the selected avatar image immediately
  const handleAvatarSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setAvatarError('');

    const validExt = /\.(png|jpe?g)$/i.test(file.name);
    if (!validExt) {
      setAvatarError('Only PNG and JPG images are allowed.');
      e.target.value = '';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setAvatarError('Image must be smaller than 5MB.');
      e.target.value = '';
      return;
    }

    setUploadingAvatar(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await uploadAvatar(formData);
      setProfile((prev) => ({ ...prev, hasAvatar: !!res.data.profile_picture_path }));
      setAvatarVersion(Date.now());

      // Keep localStorage in sync so AdminLayout topbar updates too
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      localStorage.setItem('user', JSON.stringify({
        ...storedUser,
        profile_picture_path: res.data.profile_picture_path,
        user_id: res.data.user_id,
      }));
    } catch (error) {
      if (error.response && error.response.data && error.response.data.detail) {
        setAvatarError(error.response.data.detail);
      } else {
        setAvatarError('Failed to upload photo. Please try again.');
      }
    } finally {
      setUploadingAvatar(false);
      e.target.value = '';
    }
  };

  const handleDeleteAvatar = async () => {
    setAvatarError('');
    setDeletingAvatar(true);
    try {
      await deleteAvatar();
      setProfile((prev) => ({ ...prev, hasAvatar: false }));
      setAvatarVersion(Date.now());
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      localStorage.setItem('user', JSON.stringify({
        ...storedUser,
        profile_picture_path: null
      }));
      window.dispatchEvent(new Event('avatarUpdated'));
    } catch (error) {
      setAvatarError('Failed to delete photo. Please try again.');
    } finally {
      setDeletingAvatar(false);
    }
  };

  const avatarSrc = profile.hasAvatar && profile.userId
    ? `${getAvatarUrl(profile.userId, profile.profile_picture_path)}?v=${avatarVersion}`
    : `https://ui-avatars.com/api/?name=${encodeURIComponent(profile.fullName || 'Admin')}&background=0D3B2E&color=fff&size=128`;

  if (loading) {
    return <ProfileSkeleton />;
  }

  return (
    <div className="space-y-6">
      {saved && (
        <div className="bg-green-50 border border-green-200 rounded-xl px-5 py-3 text-green-700 text-sm font-medium max-w-4xl">
          Profile updated successfully.
        </div>
      )}

      {/* Main card */}
      <div className="bg-white rounded-[20px] border border-gray-200 shadow-sm overflow-hidden max-w-4xl">
        <div className="p-8 flex flex-col md:flex-row gap-8 items-start">

          {/* ── Avatar card ── */}
          <div className="w-full md:w-[220px] flex-shrink-0 border border-gray-100 rounded-[16px] p-6 flex flex-col items-center shadow-sm bg-gray-50/50">
            <label className="relative w-24 h-24 rounded-full overflow-hidden border-4 border-white shadow-md mb-4 cursor-pointer group">
              <img src={avatarSrc} alt="Admin avatar" className="w-full h-full object-cover" />
              {/* Hover overlay with camera icon */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center text-white opacity-0 group-hover:opacity-100">
                {uploadingAvatar ? (
                  <span className="text-[10px] font-bold">Uploading...</span>
                ) : (
                  <CameraIcon />
                )}
              </div>
              <input
                type="file"
                accept=".png,.jpg,.jpeg"
                className="hidden"
                onChange={handleAvatarSelect}
                disabled={uploadingAvatar}
              />
            </label>
            {avatarError && (
              <p className="text-red-500 text-[11px] text-center mb-2">{avatarError}</p>
            )}
            {isEditing && profile.hasAvatar && (
              <button
                type="button"
                onClick={handleDeleteAvatar}
                disabled={deletingAvatar || uploadingAvatar}
                className="mt-2 text-[11px] font-bold text-red-500 hover:text-red-700 transition-colors disabled:opacity-60"
              >
                {deletingAvatar ? 'Removing...' : 'Remove photo'}
              </button>
            )}
            <p className="text-[15px] font-bold text-gray-900 text-center leading-snug">{profile.fullName}</p>
            <p className="text-[11px] text-gray-500 font-medium mt-0.5 text-center">{profile.position}</p>
            <p className="text-[11px] text-gray-400 font-medium mt-2 text-center flex items-center space-x-1">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <span>{profile.email}</span>
            </p>
            <p className="text-[10px] text-gray-300 font-medium mt-3 text-center">Click photo to change</p>
          </div>

          {/* ── Info / Edit pane ── */}
          <div className="flex-1 min-w-0">
            {!isEditing ? (
              /* ── VIEW mode ── */
              <div>
                <div className="flex justify-between items-center mb-5 gap-4">
                  <p className="text-[13px] font-bold text-gray-400 uppercase tracking-wider">Personal Information</p>
                  <button
                    onClick={() => setIsEditing(true)}
                    className="flex items-center justify-center space-x-1.5 bg-[#054334] hover:bg-[#086C53] text-white text-xs font-bold px-4 py-2 rounded-lg transition-colors shadow-sm flex-shrink-0"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    <span>Edit Profile</span>
                  </button>
                </div>

                <div className="space-y-4">
                  {[
                    { label: 'Full Name', value: profile.fullName },
                    { label: 'Email', value: profile.email },
                    { label: 'Password', value: 'Password has been set' },
                  ].map(({ label, value }) => (
                    <div key={label}>
                      <p className="text-[13px] font-bold text-gray-800">{label}</p>
                      <p className={`text-[13px] mt-0.5 ${label === 'Password' ? 'text-gray-400 italic' : 'text-gray-500'}`}>{value}</p>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              /* ── EDIT mode ── */
              <div>
                <p className="text-[13px] font-bold text-gray-400 uppercase tracking-wider mb-5">Personal Information</p>

                {submitError && (
                  <div className="mb-4 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-600 text-[12px] font-medium">
                    {submitError}
                  </div>
                )}

                <div className="space-y-4 max-w-md">
                  <div>
                    <label className="block text-[13px] font-bold text-gray-800 mb-2">Full Name</label>
                    <input
                      type="text"
                      name="fullName"
                      value={form.fullName}
                      onChange={handleChange}
                      className={`w-full bg-[#f8fafc] border ${errors.fullName ? 'border-red-400' : 'border-gray-100'} rounded-xl px-5 py-3.5 text-[14px] text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#086C53]/30 transition`}
                    />
                    {errors.fullName && <p className="text-red-500 text-[11px] mt-1">{errors.fullName}</p>}
                  </div>

                  <PasswordField
                    label="Current Password"
                    name="password"
                    value={form.password}
                    onChange={handleChange}
                    showPw={showPw.password}
                    onTogglePw={() => togglePw('password')}
                    error={errors.password}
                  />
                  <PasswordField
                    label="New Password"
                    name="newPassword"
                    value={form.newPassword}
                    onChange={handleChange}
                    showPw={showPw.newPassword}
                    onTogglePw={() => togglePw('newPassword')}
                    error={errors.newPassword}
                  />
                  <div className="mt-1.5 space-y-1">
                    <p className={`text-[11px] font-medium flex items-center gap-1 ${
                      form.newPassword.length >= 8 ? 'text-green-500' : 'text-gray-400'
                    }`}>
                      <span>{form.newPassword.length >= 8 ? '✓' : '○'}</span>
                      At least 8 characters
                    </p>
                    <p className={`text-[11px] font-medium flex items-center gap-1 ${
                      /[A-Z]/.test(form.newPassword) ? 'text-green-500' : 'text-gray-400'
                    }`}>
                      <span>{/[A-Z]/.test(form.newPassword) ? '✓' : '○'}</span>
                      At least one uppercase letter
                    </p>
                    <p className={`text-[11px] font-medium flex items-center gap-1 ${
                      /[0-9]/.test(form.newPassword) ? 'text-green-500' : 'text-gray-400'
                    }`}>
                      <span>{/[0-9]/.test(form.newPassword) ? '✓' : '○'}</span>
                      At least one number
                    </p>
                  </div>
                  <PasswordField
                    label="Confirm New Password"
                    name="confirmPassword"
                    value={form.confirmPassword}
                    onChange={handleChange}
                    showPw={showPw.confirmPassword}
                    onTogglePw={() => togglePw('confirmPassword')}
                    error={errors.confirmPassword}
                  />
                  <p className="text-[11px] text-gray-400">Leave password fields blank to keep your current password.</p>

                  <div className="flex space-x-3 pt-2">
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="px-6 py-2.5 bg-[#054334] hover:bg-[#086C53] text-white text-sm font-bold rounded-lg transition-colors shadow-sm disabled:opacity-60"
                    >
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={handleCancel}
                      disabled={saving}
                      className="px-6 py-2.5 border border-gray-200 text-gray-600 text-sm font-bold rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminProfile;